import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Sensor } from '@models';
import { DashboardInfo } from '@models/Dashboard/dashboardInfo.model';
import { DashboardWizardState } from '@models/Dashboard/dashboardWizard.model';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { MapDetails } from '@models/MapFavorite/mapDetails.model';
import { MapFavoriteInfo } from '@models/MapFavorite/mapFavoriteInfo.model';
import { MapLabelInfo } from '@models/MapFavorite/mapLabelInfo.model';
import { SensorReadingSelection } from '@models/Sensor/sensorReadingSelection.model';
import { UserGroup } from '@models/User/usergroup.model';
import { MapFavoriteService } from '@services/mapFavorite.service';
import { SensorService } from '@services/sensor.service';
import { SelectionReadingsLoader } from 'app/shared/classes/selectionReadingsLoader';
import { SensorReadingSelectorComponent } from 'app/shared/components/sensor-reading-selector/sensor-reading-selector.component';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

// What the container feeds to the right-pane preview map.
export interface MapWizardPreviewInputs {
  sensors: Sensor[];
  sensorReadings: SensorReadingInfo[];
  // The view and popups of the favorite being edited, applied to the map together with
  // its readings. Null when creating.
  savedMapDetails: MapDetails | null;
}

// The Map Favorite flow: steps 2-4 of the create wizard.
//   2 - sensors, reading types and dates (the shared selector panel)
//   3 - arrange the map: pan/zoom, open and drag pin popups, add labels (all done on the
//       right-pane preview map; labels are drafts held in wizard state)
//   4 - save (name, access group, new/existing dashboard)
// Nothing is persisted here; the container saves everything on Finish.
@Component({
  standalone: false,
  selector: 'map-favorite-wizard-flow',
  templateUrl: './map-favorite-wizard-flow.component.html',
  styleUrls: ['../chart-favorite-flow/chart-favorite-wizard-flow.component.scss', './map-favorite-wizard-flow.component.scss']
})
export class MapFavoriteWizardFlowComponent implements OnInit, OnChanges, OnDestroy {
  @Input() step = 2;
  @Input() state: DashboardWizardState;
  @Input() dashboards: DashboardInfo[] = [];
  @Input() associatedUserGroups: UserGroup[] = [];
  @Input() username = '';
  // When set, the wizard edits this existing map favorite (pre-load its values).
  @Input() editMapFavorite: MapFavoriteInfo = null;
  @Input() isEditMode = false;

  @Output() stepValidChange = new EventEmitter<boolean>();
  @Output() previewChanged = new EventEmitter<MapWizardPreviewInputs>();

  // static: the selector is never inside a structural directive here (it is hidden, not
  // removed, off step 2, so it keeps its selection), and edit mode needs it in ngOnInit.
  @ViewChild('selector', { static: true }) selector: SensorReadingSelectorComponent;

  readonly userGroupConfig: any = {
    displayKey: 'name',
    search: true,
    height: '220px',
    placeholder: 'Select Access Group',
    searchOnKey: 'name'
  };

  private loader: SelectionReadingsLoader;
  private readings: SensorReadingInfo[] = [];
  private loadSubscription: Subscription | null = null;
  private savedDetails: MapDetails | null = null;
  private existingFavoriteNames: string[] = [];

  constructor(private sensorService: SensorService,
              private mapFavoriteService: MapFavoriteService,
              private toastr: ToastrService) {
    this.loader = new SelectionReadingsLoader(sensorService);
  }


  ngOnInit(): void {
    this.loadMapFavoriteNames();
    if (this.isEditMode && this.editMapFavorite != undefined) {
      this.applyEditMapFavorite();
    } else if (this.m.favoriteUserGroup == undefined) {
      this.m.favoriteUserGroup = this.associatedUserGroups.find(u => u.id == -1) ?? null;
    }
    this.recomputeValidity();
    this.emitPreview();
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['step'] && !changes['step'].firstChange) {
      this.recomputeValidity();
    }
  }


  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.loader.cancel();
  }


  // Shorthand to the map-favorite slice of the shared wizard state.
  get m() {
    return this.state.mapFavorite;
  }


  // True when the entered name already exists (case-insensitive). When editing, the
  // favorite's own current name is allowed.
  get favoriteNameTaken(): boolean {
    const name = (this.m.favoriteName || '').trim().toLowerCase();
    if (name === '') { return false; }
    if (this.isEditMode && this.editMapFavorite && name === (this.editMapFavorite.name || '').trim().toLowerCase()) {
      return false;
    }
    return this.existingFavoriteNames.includes(name);
  }


  get newDashboardNameTaken(): boolean {
    const name = (this.m.newDashboardName || '').trim().toLowerCase();
    if (this.m.dashboardTargetMode !== 'new' || name === '') {
      return false;
    }
    return this.dashboards.some(d => d.name && d.name.trim().toLowerCase() === name);
  }


  get dynamicLabelCount(): number {
    return this.m.labels.filter(l => l.isDynamic).length;
  }


  get hasSelection(): boolean {
    return this.m.selectedSensors.length > 0 && this.m.selectedReadingTypes.length > 0;
  }


  // ---------- data loading ----------

  private loadMapFavoriteNames(): void {
    this.mapFavoriteService.getMapFavorites().subscribe(
      favorites => {
        this.existingFavoriteNames = (favorites || []).map(f => (f.name || '').trim().toLowerCase());
        this.recomputeValidity();
      },
      () => { /* non-fatal: the server still enforces nothing here, the page just cannot pre-check */ }
    );
  }


  // Seed the wizard state from the favorite being edited. The selector restores the
  // sensors / reading types / dates and tells us through selectionChanged; labels are
  // copies so Cancel discards any changes.
  private applyEditMapFavorite(): void {
    const e = this.editMapFavorite;

    let details: MapDetails | null = null;
    try {
      details = (e.mapDetails != undefined && e.mapDetails !== '') ? JSON.parse(e.mapDetails) : null;
    } catch {
      details = null;
    }
    this.savedDetails = details;
    this.m.mapDetails = details;

    this.m.favoriteName = e.name;
    this.m.favoriteUserGroup = this.associatedUserGroups.find(u => u.id == (e.userGroupId ?? -1))
      ?? this.associatedUserGroups.find(u => u.id == -1) ?? null;

    this.selector.applySelection({
      sensorIds: (e.sensors ?? []).map(s => s.id),
      sensorGroupIds: (e.sensorGroups ?? []).map(g => g.id),
      readingTypeIds: (e.sensorReadingTypes ?? []).map(t => t.id),
      dateRange: e.dateRange,
      timeZone: details?.timeZone
    });

    this.mapFavoriteService.getMapLabels(e.id).subscribe(
      (labels: MapLabelInfo[] | null) => {
        this.m.labels = (labels ?? []).map(l => ({ ...l }));
        this.m.originalLabelIds = this.m.labels.map(l => l.id);
        this.recomputeValidity();
      },
      () => this.toastr.error('Unable to load the map favorite\'s labels.')
    );
  }


  // ---------- step 2: the shared selector ----------

  onSelectionChanged(selection: SensorReadingSelection): void {
    const m = this.m;
    m.selectedSensors = selection.sensors;
    m.selectedSensorGroups = selection.sensorGroups;
    m.availableReadingTypes = selection.availableReadingTypes;
    m.selectedReadingTypes = selection.readingTypes;
    m.dateRange = selection.dateRange;
    m.dateRangeName = selection.dateRangeName;
    m.selectedTimeZone = selection.timeZone;
    m.selectedTimeZoneOffset = selection.timeZoneOffset;

    if (selection.reason !== 'readingTypes' && selection.reason !== 'source') {
      // The cached readings belong to the previous sensors / dates / time zone.
      this.loader.clear();
    }

    this.recomputeValidity();
    this.loadReadings();
  }


  refreshReadings(): void {
    this.loader.clear();
    this.loadReadings();
  }


  private loadReadings(): void {
    const m = this.m;
    this.loadSubscription?.unsubscribe();

    this.loadSubscription = this.loader.load({
      sensors: m.selectedSensors,
      readingTypes: m.selectedReadingTypes,
      availableReadingTypes: m.availableReadingTypes,
      dateRange: m.dateRange,
      timeZone: m.selectedTimeZone,
      timeZoneOffset: m.selectedTimeZoneOffset
    }).subscribe(
      result => {
        if (result == null) {
          return;   // a newer selection superseded this request
        }
        this.readings = result.readings;
        this.emitPreview();

        if (result.fetched && result.fetchedCount === 0) {
          this.toastr.warning('No sensor readings found for the selection.');
        }
      },
      err => {
        this.toastr.error('Error occurred while retrieving sensor readings. Error: ' + err.message);
      });
  }


  private emitPreview(): void {
    this.previewChanged.emit({
      sensors: this.m.selectedSensors,
      sensorReadings: this.readings,
      savedMapDetails: this.savedDetails
    });
  }


  onSaveFieldChanged(): void {
    this.recomputeValidity();
  }


  // Labels change on the preview (owned by the container); re-validate.
  onLabelsChanged(): void {
    this.recomputeValidity();
  }


  // ---------- validation (gates the container's Next / Finish) ----------

  private recomputeValidity(): void {
    let valid = false;
    switch (this.step) {
      case 2:
        valid = this.hasSelection;
        break;
      case 3:
        // Labels are optional; a dynamic label needs its sensor + reading type.
        valid = this.m.labels.every(l => !l.isDynamic || (l.sensorID != undefined && l.sensorReadingTypeID != undefined));
        break;
      case 4: {
        // The selection is re-checked because an edit restores it asynchronously and
        // the stepper lets the user jump straight to the last step.
        const nameOk = this.hasSelection && !!this.m.favoriteName && this.m.favoriteName.trim().length > 0 && !this.favoriteNameTaken;
        valid = this.isEditMode
          ? nameOk
          : nameOk && (this.m.dashboardTargetMode === 'existing'
              ? this.m.targetDashboard != null
              : !!this.m.newDashboardName && this.m.newDashboardName.trim().length > 0 && !this.newDashboardNameTaken);
        break;
      }
      default:
        valid = true;
    }
    this.stepValidChange.emit(valid);
  }
}
